import type { RootState } from 'src/redux/store';
import type { UseFormSetValue } from 'react-hook-form';
import type { IContact } from 'src/pages/contacts/contacts.types';
import type { IAccount } from 'src/pages/accounts/accounts.types';

import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import {
  useGetUserDtlMutation,
  useGetTeamDetailsMutation,
  useFilterUserDetailMutation,
  useGetFilterTeamListMutation,
} from 'src/pages/setting/slice/settingApi';

import {
  type ITicket,
  defaultFilter,
  type ITicketCreate,
  type IDefaultFilter,
} from '../tickets.types';

interface IRecipient {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface IsetQueryParamsHandler {
  type: 'search' | 'pagination' | 'reset';
  value?: string;
}

interface IUseGetAssigneeList {
  selectedTicket?: ITicket | IContact | IAccount | undefined;
  setValue?:
    | UseFormSetValue<ITicketCreate>
    | UseFormSetValue<{
        [key: string]:
          | string
          | {
              assignedId: string;
              assignedType: string;
            }
          | undefined;
      }>;
  singleFieldAssigee?: { assignedId: string; assignedType: string }; // Using for [Detail] single field assignee
  multiFieldAssign?: any;
  isDidPage?: boolean;
  isTeamPage?: boolean;
}

export const useGetAssigneeList = ({
  selectedTicket,
  setValue,
  singleFieldAssigee,
  multiFieldAssign,
  isDidPage = false,
  isTeamPage = false,
}: IUseGetAssigneeList) => {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const updatedFilter = {
    ...defaultFilter,
    filters: [...defaultFilter.filters, { fieldname: 'status', search: 'ACTIVE' }],
  };

  const [filterUserList] = useFilterUserDetailMutation();
  const [getFilterTeamList] = useGetFilterTeamListMutation();
  const [getTeamDetails] = useGetTeamDetailsMutation();
  const [getUserDetails] = useGetUserDtlMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const initialPaginationVal = { fetched: 0, total: Infinity };

  const [_recordList, setRecordList] = useState<IRecipient[]>([]);
  const [userPagination, setUserPagination] = useState(initialPaginationVal);
  const [teamPagination, setTeamPagination] = useState(initialPaginationVal);
  const [queryParams, setQueryParams] = useState<IDefaultFilter>(updatedFilter);
  const [isFetching, setIsFetching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedAssigneeDtl, setSelectedAssigneeDtl] = useState<any>(null);
  const [open, setOpen] = useState(false);

  const openListHandler = () => setOpen(true);
  const closeListHandler = () => {
    setOpen(false);
    queryParamsHandler({ type: 'reset' });
  };

  const selectedAssigneeHandler = (selectedAssignee: any) => {
    setSelectedAssigneeDtl(selectedAssignee);
  };

  const searchValueHandler = (value: string) => setSearchValue(value);

  const resetPagination = () => {
    setUserPagination(initialPaginationVal);
    setTeamPagination(initialPaginationVal);
  };

  const queryParamsHandler = ({ type, value }: IsetQueryParamsHandler) => {
    switch (type) {
      case 'search': {
        setQueryParams((prev) => {
          if (prev.globalSearch !== value) {
            resetPagination();
            return { ...prev, page: 1, globalSearch: value || '' };
          }
          return prev;
        });
        break;
      }
      case 'pagination':
        setQueryParams((prev) => ({ ...prev, page: prev.page + 1 }));
        break;
      case 'reset':
        setQueryParams(updatedFilter);
        break;
      default:
        break;
    }
  };

  const listScrollHandler = (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget;
      const isBottom =  target.scrollTop + target.clientHeight >= target.scrollHeight -5;

    const userHasMore = userPagination.fetched < userPagination.total;
    const teamHasMore = teamPagination.fetched < teamPagination.total;

    if (isBottom && !isFetching && (userHasMore || teamHasMore)) {
      queryParamsHandler({ type: 'pagination' });
    }
  };

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const getAssigneeListFunc = useCallback(
    async (params: IDefaultFilter) => {
      const labelText = (assignee: any) =>
        assignee?.users
          ? (assignee?.teamName ?? '')
          : assignee?.firstName || assignee?.lastName
            ? `${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim()
            : (assignee.email ?? '');

      try {
        setIsFetching(true);

        const promises = [];

        const getRecordList = async (list = []) => {
          if (list && list.length > 0) {
            const updatedList = list.map(async (record: any) => ({
              ...record,
              name: labelText(record),
              isUser: !record.users,
              ProfileURL: await getProfileUrl(record?.ProfileURL ?? ''),
            }));

            return await Promise.all(updatedList);
          }
          return [];
        };

        if (userPagination.fetched < userPagination.total && !isDidPage) {
          promises.push(
            filterUserList(params)
              .unwrap()
              .then(async (res) => {
                setUserPagination({
                  fetched: userPagination.fetched + res.list.length,
                  total: res.total,
                });
                return { list: await getRecordList(res.list ?? []), total: res.total };
              })
          );
        } else {
          promises.push(Promise.resolve({ list: [], total: 0 }));
        }

        if (teamPagination.fetched < teamPagination.total && !isTeamPage) {
          promises.push(
            getFilterTeamList(params)
              .unwrap()
              .then((res) => {
                setTeamPagination({
                  fetched: teamPagination.fetched + res.list.length,
                  total: res.total,
                });
                return {
                  list: res.list.map((team: any) => ({
                    ...team,
                    name: labelText(team),
                    isUser: false,
                  })),
                  total: res.total,
                };
              })
          );
        } else {
          promises.push(Promise.resolve({ list: [], total: 0 }));
        }

        const [userList, teamList] = await Promise.all(promises);

        const combinedList = [...userList.list, ...teamList.list];

        const filterAssignee = (list: any[], id: string) => {
          if (!selectedAssigneeDtl) return list;
          return list.filter((item) => item.id !== id);
        };

        const filteredList = filterAssignee(combinedList, selectedAssigneeDtl?.id ?? '');

        // setRecordList((prev: any) =>
        //   params.globalSearch ? [...combinedList] : [...prev, ...filteredList]
        // );

        setRecordList((prev: any) => {
          const newList = params.globalSearch ? filteredList : [...prev, ...filteredList];
          const seen = new Set();
          return newList.filter((item) => {
            const isDup = seen.has(item.id);
            seen.add(item.id);
            return !isDup;
          });
        });
      } catch (error) {
        setIsFetching(false);
        console.error('Error fetching list:', error);
        toast.error('List Fetching Failed');
      } finally {
        setIsFetching(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAssigneeDtl, userPagination, teamPagination]
  );

  const getAssignedDetails = useCallback((assignedId: string, assignedType: string) => {
    if (!assignedId && !assignedType) return;
    if (loginApiData && 'id' in loginApiData && loginApiData.id) {
      const apiFuncObj = { USER: getUserDetails, TEAM: getTeamDetails };

      const labelText = (assignee: any) =>
        assignee?.users
          ? (assignee?.name ?? '')
          : assignee?.firstName || assignee?.lastName
            ? `${assignee?.firstName ?? ''} ${assignee?.lastName ?? ''}`.trim()
            : (assignee?.email ?? '');

      try {
        apiFuncObj[assignedType as keyof typeof apiFuncObj]({ id: assignedId }).then(
          async (res: any) => {
            if (res && res?.data) {
              const updatedResp = {
                ...res?.data,
                name: labelText(res?.data),
                isUser: assignedType?.toUpperCase() === 'USER',
                ProfileURL: '',
              };

              if (res?.data?.ProfileURL) {
                updatedResp['ProfileURL'] = await getProfileUrl(res?.data?.ProfileURL);
              }

              setSelectedAssigneeDtl(updatedResp);
              if (setValue) (setValue as UseFormSetValue<any>)('assignedId', updatedResp);
            }
          }
        );
      } catch (error) {
        toast.error('Record Detail Fetching Failed');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      queryParamsHandler({ type: 'search', value: searchValue });
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      getAssigneeListFunc(queryParams);
    } else {
      let updatedList = [];

      if (selectedAssigneeDtl) {
        if (multiFieldAssign) {
          // Assume selectedAssigneeDtl is already an array or compatible format
          updatedList = Array.isArray(selectedAssigneeDtl)
            ? selectedAssigneeDtl
            : [selectedAssigneeDtl];
        } else {
          // Only one item allowed
          updatedList = [selectedAssigneeDtl];
        }
      }

      setRecordList(updatedList);
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryParams, selectedAssigneeDtl]);

  // This useEffect only runs when the user open update ticket modal
  useEffect(() => {
    if (selectedTicket && selectedTicket?.assignedId && selectedTicket?.assignedType) {
      getAssignedDetails(selectedTicket?.assignedId, selectedTicket?.assignedType?.toUpperCase());
    }
    return () => {
      setSelectedAssigneeDtl(null);
    };
  }, [selectedTicket]);


  useEffect(() => {
    if (singleFieldAssigee) {
      getAssignedDetails(singleFieldAssigee?.assignedId, singleFieldAssigee?.assignedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleFieldAssigee?.assignedId]);

  const handleResetSelectedAssignee = ({
    assignedId,
    assignedType,
  }: any) => {
    getAssignedDetails(assignedId, assignedType);
  };

  useEffect(() => {
    setSelectedAssigneeDtl(multiFieldAssign);
  }, [multiFieldAssign]);

  return {
    _recordList,
    isFetching,
    searchValue,
    open,
    resetPagination,
    queryParamsHandler,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
    selectedAssigneeHandler,
    handleResetSelectedAssignee,
    selectedAssigneeDtl,
  };
};
